// src/components/MediaCarousel.jsx
// Reusable carousel for service media (images + videos) with swipe support,
// dots indicator, video autoplay/pause, and lightbox zoom integration.
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FiChevronRight, FiChevronLeft, FiPlay, FiZoomIn } from 'react-icons/fi';

// ── Helpers ────────────────────────────────────────────────
export const isVideoUrl = (url = '') => {
  if (!url) return false;
  const clean = url.split('?')[0].toLowerCase();
  return (
    clean.endsWith('.mp4') ||
    clean.endsWith('.webm') ||
    clean.endsWith('.ogg') ||
    clean.endsWith('.mov') ||
    clean.endsWith('.m4v') ||
    clean.includes('/video/') ||
    clean.includes('video=')
  );
};

export const isImageUrl = (url = '') => {
  if (!url) return false;
  const clean = url.split('?')[0].toLowerCase();
  return (
    clean.endsWith('.jpg') ||
    clean.endsWith('.jpeg') ||
    clean.endsWith('.png') ||
    clean.endsWith('.gif') ||
    clean.endsWith('.webp') ||
    clean.endsWith('.svg') ||
    clean.endsWith('.bmp') ||
    clean.includes('/image/') ||
    clean.includes('image=')
  );
};

// Build a unified media list from service data
export const buildMediaList = (service = {}) => {
  const list = [];
  const push = (url) => {
    if (!url) return;
    const s = String(url).trim();
    if (!s) return;
    // Avoid duplicates
    if (list.some((m) => m.url === s)) return;
    list.push({ url: s, type: isVideoUrl(s) ? 'video' : 'image' });
  };

  // 1. media_urls array (new)
  (service.mediaUrls || service.media_urls || []).forEach(push);

  // 2. Fallback: image_url as first slide if not already present
  push(service.imageUrl || service.image_url);

  // 3. preview_images (digital products)
  (service.previewImages || service.preview_images || []).forEach(push);

  return list;
};

// ── MediaCarousel ──────────────────────────────────────────
const MediaCarousel = ({
  media = [],
  title = '',
  height = 'h-64 sm:h-80',
  objectFit = 'object-contain',
  showDots = true,
  showArrows = true,
  autoPlayVideos = true,
  onImageClick = null, // custom handler; default opens lightbox
  lightboxEnabled = true,
  className = '',
}) => {
  const [current, setCurrent] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const videoRefs = useRef({});

  const count = media.length;

  // Reset current when media changes
  useEffect(() => {
    setCurrent(0);
  }, [media.length]);

  // Auto-play / pause videos based on visibility
  useEffect(() => {
    if (!autoPlayVideos) return;
    Object.entries(videoRefs.current).forEach(([idx, video]) => {
      if (!video) return;
      if (Number(idx) === current) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }, [current, autoPlayVideos, count]);

  const goTo = useCallback((i) => {
    if (count === 0) return;
    setCurrent(((i % count) + count) % count);
  }, [count]);

  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowLeft') next();
      if (e.key === 'ArrowRight') prev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [next, prev]);

  // ── Touch / Swipe handlers ──────────────────────────────
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchStartX.current = t.clientX;
    touchStartY.current = t.clientY;
  };

  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartX.current;
    const dy = t.clientY - touchStartY.current;
    touchStartX.current = null;
    touchStartY.current = null;

    // Only treat as horizontal swipe if horizontal movement dominates
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
      if (dx < 0) next(); // swipe left → next
      else prev();        // swipe right → prev
    }
  };

  const openLightbox = (idx) => {
    if (!lightboxEnabled) return;
    if (onImageClick) {
      onImageClick(idx);
      return;
    }
    setLightboxIndex(idx);
    setLightboxOpen(true);
  };

  if (count === 0) {
    return (
      <div className={`relative w-full ${height} bg-gray-100 flex items-center justify-center ${className}`}>
        <div className="text-gray-300 text-sm">لا توجد وسائط</div>
      </div>
    );
  }

  const currentMedia = media[current];

  return (
    <>
      <div
        className={`relative w-full ${height} bg-gray-100 overflow-hidden select-none ${className}`}
        dir="rtl"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {/* Slide container — no gradient overlays that block gestures */}
        <div
          className="w-full h-full flex transition-transform duration-300 ease-out"
          style={{ transform: `translateX(${current * 100}%)` }}
        >
          {media.map((m, i) => (
            <div
              key={i}
              className="w-full h-full flex-shrink-0 flex items-center justify-center relative"
              style={{ pointerEvents: i === current ? 'auto' : 'none' }}
            >
              {m.type === 'video' ? (
                <video
                  ref={(el) => { videoRefs.current[i] = el; }}
                  src={m.url}
                  className={`w-full h-full ${objectFit}`}
                  controls
                  playsInline
                  muted
                  loop
                  preload="metadata"
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <img
                  src={m.url}
                  alt={`${title || 'وسائط'} ${i + 1}`}
                  className={`w-full h-full ${objectFit} cursor-zoom-in`}
                  loading="lazy"
                  draggable={false}
                  onClick={() => openLightbox(i)}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
            </div>
          ))}
        </div>

        {/* Counter badge */}
        {count > 1 && (
          <span className="absolute top-3 left-3 z-10 bg-black/50 text-white text-[10px] font-bold px-2 py-0.5 rounded-full backdrop-blur-sm pointer-events-none">
            {current + 1} / {count}
          </span>
        )}

        {/* Video badge */}
        {currentMedia?.type === 'video' && (
          <span className="absolute top-3 right-3 z-10 bg-black/50 text-white text-[10px] font-bold px-2 py-0.5 rounded-full backdrop-blur-sm flex items-center gap-1 pointer-events-none">
            <FiPlay size={9} /> فيديو
          </span>
        )}

        {/* Arrows — pointer-events-auto to ensure clickable */}
        {showArrows && count > 1 && (
          <>
            <button
              onClick={prev}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center transition pointer-events-auto active:scale-90"
              aria-label="السابق"
            >
              <FiChevronRight size={18} />
            </button>
            <button
              onClick={next}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center transition pointer-events-auto active:scale-90"
              aria-label="التالي"
            >
              <FiChevronLeft size={18} />
            </button>
          </>
        )}

        {/* Dots — pointer-events-auto */}
        {showDots && count > 1 && (
          <div className="absolute bottom-2 inset-x-0 z-10 flex justify-center gap-1.5 pointer-events-none">
            {media.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`pointer-events-auto rounded-full transition-all duration-300 ${
                  i === current ? 'w-5 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-white/50'
                }`}
                aria-label={`الانتقال إلى ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <MediaLightbox
          media={media}
          initialIndex={lightboxIndex}
          title={title}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
};

// ── MediaLightbox (fullscreen zoom) ────────────────────────
export const MediaLightbox = ({ media, initialIndex = 0, title = '', onClose }) => {
  const [index, setIndex] = useState(initialIndex);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, tx: 0, ty: 0 });
  const pinchDist = useRef(null);
  const containerRef = useRef(null);

  const count = media.length;
  const currentMedia = media[index];

  // Reset zoom when index changes
  useEffect(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, [index]);

  // Keyboard
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') setIndex((i) => (i + 1) % count);
      if (e.key === 'ArrowRight') setIndex((i) => (i - 1 + count) % count);
      if (e.key === '+' || e.key === '=') setScale((s) => Math.min(4, s + 0.5));
      if (e.key === '-') setScale((s) => Math.max(0.5, s - 0.5));
      if (e.key === '0') { setScale(1); setTranslate({ x: 0, y: 0 }); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [count, onClose]);

  // Mouse wheel zoom
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (e) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.2 : 0.2;
      setScale((s) => Math.min(4, Math.max(0.5, s + delta)));
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, []);

  // ── Touch: pinch zoom + pan ─────────────────────────────
  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchDist.current = Math.sqrt(dx * dx + dy * dy);
      return;
    }
    if (e.touches.length === 1) {
      setDragging(true);
      dragStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, tx: translate.x, ty: translate.y };
    }
  };

  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinchDist.current) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const ratio = dist / pinchDist.current;
      pinchDist.current = dist;
      setScale((s) => Math.min(4, Math.max(0.5, s * ratio)));
      return;
    }
    if (dragging && e.touches.length === 1) {
      const dx = e.touches[0].clientX - dragStart.current.x;
      const dy = e.touches[0].clientY - dragStart.current.y;
      setTranslate({ x: dragStart.current.tx + dx, y: dragStart.current.ty + dy });
    }
  };

  const onTouchEnd = () => {
    pinchDist.current = null;
    setDragging(false);
  };

  // ── Mouse drag pan (when zoomed) ────────────────────────
  const onMouseDown = (e) => {
    if (scale <= 1) return;
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, tx: translate.x, ty: translate.y };
  };

  const onMouseMove = (e) => {
    if (!dragging || scale <= 1) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    setTranslate({ x: dragStart.current.tx + dx, y: dragStart.current.ty + dy });
  };

  const onMouseUp = () => setDragging(false);

  const resetZoom = () => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  };

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[100] bg-black/95 flex flex-col"
      dir="rtl"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      style={{ touchAction: 'none' }}
    >
      {/* Header */}
      <div className="flex items-center gap-3 p-4 flex-shrink-0 z-10">
        <button
          onClick={onClose}
          className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/10 text-white hover:bg-white/20 transition flex-shrink-0"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
        <p className="flex-1 text-sm font-bold text-white truncate">{title}</p>
        <span className="text-xs text-white bg-white/15 px-3 py-1 rounded-full font-semibold">
          {index + 1} / {count}
        </span>
      </div>

      {/* Media area */}
      <div className="flex-1 flex items-center justify-center overflow-hidden relative">
        {currentMedia?.type === 'video' ? (
          <video
            src={currentMedia.url}
            className="max-w-full max-h-full object-contain"
            controls
            autoPlay
            playsInline
            style={{ transform: `scale(${scale}) translate(${translate.x}px, ${translate.y}px)`, transition: dragging ? 'none' : 'transform .2s' }}
          />
        ) : (
          <img
            src={currentMedia.url}
            alt={`${title || 'وسائط'} ${index + 1}`}
            className="max-w-full max-h-full object-contain select-none"
            draggable={false}
            style={{
              transform: `scale(${scale}) translate(${translate.x}px, ${translate.y}px)`,
              transition: dragging ? 'none' : 'transform .2s',
              cursor: scale > 1 ? 'grabbing' : 'zoom-in',
            }}
            onDoubleClick={() => (scale > 1 ? resetZoom() : setScale(2.5))}
          />
        )}

        {/* Zoom controls */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          <button
            onClick={() => setScale((s) => Math.min(4, s + 0.5))}
            className="w-9 h-9 rounded-xl bg-white/10 border border-white/10 text-white text-sm font-bold hover:bg-white/20 transition flex items-center justify-center"
          >
            +
          </button>
          <button
            onClick={resetZoom}
            className="w-9 h-9 rounded-xl bg-white/10 border border-white/10 text-white text-sm font-bold hover:bg-white/20 transition flex items-center justify-center"
          >
            ↺
          </button>
          <button
            onClick={() => setScale((s) => Math.max(0.5, s - 0.5))}
            className="w-9 h-9 rounded-xl bg-white/10 border border-white/10 text-white text-sm font-bold hover:bg-white/20 transition flex items-center justify-center"
          >
            −
          </button>
        </div>
      </div>

      {/* Nav arrows */}
      {count > 1 && (
        <>
          <button
            onClick={() => setIndex((i) => (i + 1) % count)}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-xl bg-white/10 border border-white/10 text-white text-xl font-bold hover:bg-white/20 transition flex items-center justify-center z-10"
          >
            ›
          </button>
          <button
            onClick={() => setIndex((i) => (i - 1 + count) % count)}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-xl bg-white/10 border border-white/10 text-white text-xl font-bold hover:bg-white/20 transition flex items-center justify-center z-10"
          >
            ‹
          </button>
        </>
      )}

      {/* Dots */}
      {count > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
          {media.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              className="cursor-pointer rounded-full transition-all"
              style={{
                width: i === index ? 20 : 6,
                height: 6,
                background: i === index ? '#fff' : 'rgba(255,255,255,0.3)',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MediaCarousel;